import { useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router'

import {
  Flex,
  TabsComponent as Tabs,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'
import { FaArrowUpFromBracketRegular } from '@enechain/ecloud-icons'

import { DsSelect } from '~/components/common/DsSelect'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PlaceholderPage } from '~/components/common/PlaceholderPage'
import { RegionLoading } from '~/components/common/RegionLoading'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
} from '~/components/common/filters'
import { iconSize } from '~/constants/iconSize'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { convertAreaLabel } from '~/models/area'
import { withCsvImportReturnTo } from '~/models/csvImportReturn'
import { deriveVisibleAreaCodes } from '~/pages/AdminLossRate/models/visibleAreas'
import { parseAreaCodeParam } from '~/pages/DemandForecastDaily/models/sectionHelpers'
import { useDemandBreakdownBoard } from '~/pages/DemandSettings/hooks/useDemandBreakdownBoard'
import { useDemandGroupDirectory } from '~/pages/DemandSettings/hooks/useDemandGroupDirectory'
import {
  type DemandSettingsTabKey,
  availableDemandSettingsTabs,
  tabAvailability,
} from '~/pages/DemandSettings/models/availableTabs'
import { BreakdownModeTab } from '~/pages/DemandSettings/tabs/BreakdownModeTab'
import { DemandGroupsTab } from '~/pages/DemandSettings/tabs/DemandGroupsTab'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'
import { routePaths } from '~/routes/routePaths'

/** URL の `?tab=` 名 */
export const DEMAND_SETTINGS_TAB_PARAM = 'tab'

/** URL の `?area=<AreaCode の値>` 名。CSV 取り込みから戻った時に選んでいたエリアで開くために使う */
export const DEMAND_SETTINGS_AREA_PARAM = 'area'

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
const FILTER_STORAGE_KEY = 'demandSettings'

/** エリアが決まっていない間 (業務エリア設定の取得前・未設定) の要約の値。 */
const NO_VALUE = '—'

/**
 * 需要想定設定画面 (`/demand/settings`、AC-DFS-001〜003)。エリアの切り替え (全タブ共通) の下に、
 * 出せるタブだけを並べる。タブが 1 つでも見出しは出す。
 * 選んだタブは URL の `?tab=` で表す (不正・未指定は出せる最初のタブ)。
 *
 * 需要グループと想定モードの公開はそれぞれ組織ごとの feature flag で、目録と想定モード一覧の
 * `featureEnabled` で分かる (`availableDemandSettingsTabs`)。出せるタブが無い組織は、画面全体を
 * PlaceholderPage にする (評価失敗も backend が false に畳む)。
 */
export const DemandSettingsPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [chosenArea, setChosenArea] = useState<AreaCode | undefined>(undefined)

  const directory = useDemandGroupDirectory()
  const board = useDemandBreakdownBoard()
  const personalSettings = useGetPersonalSettingsQuery()

  // エリアの選択肢 = 業務エリア設定 ∩ 全エリア (業務エリア設定の順)。
  // 初期値は URL の `?area=` が選択肢に含まれればそれ、無い・不正なら先頭。
  // 画面での切り替えは URL へ書き戻さない (DemandForecastDaily と同じ。戻り先は導線を作る時点のエリアを使う)。
  const urlArea = parseAreaCodeParam(
    searchParams.get(DEMAND_SETTINGS_AREA_PARAM),
  )
  const visibleAreas = deriveVisibleAreaCodes(
    personalSettings.data?.settings?.businessAreas ?? [],
  )
  const area =
    chosenArea !== undefined && visibleAreas.includes(chosenArea)
      ? chosenArea
      : urlArea !== undefined && visibleAreas.includes(urlArea)
        ? urlArea
        : visibleAreas[0]

  const tabKeys = availableDemandSettingsTabs({
    demandGroups: tabAvailability(directory),
    breakdownModes: tabAvailability(board),
  })
  const tabs: Array<{ key: DemandSettingsTabKey; label: string }> = (
    tabKeys ?? []
  ).map((key) => ({
    key,
    label:
      key === 'demand-groups'
        ? t('page:demandSettings.tab.demandGroups')
        : t('page:demandSettings.tab.breakdownMode'),
  }))

  // 公開の有無は目録と想定モード一覧で分かる。両方の取得が終わるまでは、タブもエリアも出さず
  // 画面全体を読み込み中にする (未公開の組織にタブ見出しが一瞬出てから開発中の表示へ替わる・
  // タブが後から増えるのを避ける、AC-DFS-003)
  if (tabKeys === null) {
    return (
      <PageLayout
        breadcrumbItems={[
          { label: t('page:demandSettings.breadcrumb.demand') },
          { label: t('common:route.demandSettings'), bold: true },
        ]}
      >
        <PageBody>
          <RegionLoading flex="1" isLoading>
            {null}
          </RegionLoading>
        </PageBody>
      </PageLayout>
    )
  }

  const tabParam = searchParams.get(DEMAND_SETTINGS_TAB_PARAM)
  const tab = tabs.find((candidate) => candidate.key === tabParam) ?? tabs[0]
  if (tab === undefined) {
    return <PlaceholderPage routeKey="demandSettings" />
  }

  // CSV 取り込みの戻り先には今選んでいるエリアを載せる (URL の area は初期値で、画面で切り替えても書き戻さないため)
  const returnParams = new URLSearchParams({ tab: tab.key })
  if (area !== undefined)
    returnParams.set(DEMAND_SETTINGS_AREA_PARAM, String(area))
  const csvImportLink = withCsvImportReturnTo(
    routePaths.adminUploadsDemandGroups.path,
    `${routePaths.demandSettings.path}?${returnParams.toString()}`,
  )

  const handleSelectTab = (key: DemandSettingsTabKey): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(DEMAND_SETTINGS_TAB_PARAM, key)
        return next
      },
      { replace: true },
    )
  }

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('page:demandSettings.breadcrumb.demand') },
        { label: t('common:route.demandSettings'), bold: true },
      ]}
      headerExtra={
        // CSV 取り込みは絞り込みではなく操作なので、カードの外 (画面の右上) に置く
        tab.key === 'demand-groups' ? (
          <TertiaryButton asChild size="md">
            <RouterLink to={csvImportLink}>
              <FaArrowUpFromBracketRegular boxSize={iconSize.labeledButton} />
              {t('page:adminDemandGroups.toolbar.csvUpload')}
            </RouterLink>
          </TertiaryButton>
        ) : undefined
      }
    >
      <PageBody>
        {/* エリアは見るデータの範囲を決める選択で、既定に戻す条件ではない (既定 = 先頭のエリア)。
            件数は常に 0 で、クリアは押せない状態で出す */}
        <CollapsibleFilterToolbar
          modifiedCount={0}
          storageKey={FILTER_STORAGE_KEY}
          summary={[
            {
              key: 'area',
              label: t('page:demandSettings.areaLabel'),
              value: area === undefined ? NO_VALUE : convertAreaLabel(area),
            },
          ]}
        >
          <FilterGrid
            items={[
              {
                key: 'area',
                label: t('page:demandSettings.areaLabel'),
                control: (
                  <DsSelect
                    minWidth="221px"
                    aria-label={t('page:demandSettings.areaLabel')}
                    inputId="demandSettings-area"
                    onChange={(value) =>
                      setChosenArea(Number(value) as AreaCode)
                    }
                    options={visibleAreas.map((code) => ({
                      value: String(code),
                      label: convertAreaLabel(code),
                    }))}
                    value={area === undefined ? '' : String(area)}
                  />
                ),
              },
            ]}
          />
        </CollapsibleFilterToolbar>
        <Tabs
          items={tabs}
          onSelect={handleSelectTab}
          selected={tab.key}
          tabSx={{ fontSize: tabTriggerFontSize.raw }}
        />
        <Flex flexDirection="column" flex="1" minHeight="0">
          {tab.key === 'demand-groups' && (
            <DemandGroupsTab
              area={area}
              directory={directory}
              isAreaLoading={personalSettings.isLoading}
            />
          )}
          {tab.key === 'breakdown-mode' && (
            <BreakdownModeTab
              area={area}
              board={board}
              isAreaLoading={personalSettings.isLoading}
            />
          )}
        </Flex>
      </PageBody>
    </PageLayout>
  )
}
